/**
 * Picture swatches on a product card, e.g. frame styles (see snippets/card-option-image-swatches.liquid).
 *
 * The card gallery already contains every variant image as a (hidden) slide, keyed by media ID.
 * - Hovering a swatch with a mouse previews that variant's image; leaving the swatches restores the selection.
 * - Clicking a swatch selects it: the image stays, and the card's links open the product with that variant.
 * Buttons are ignored by the card's own click-to-navigate handler, so selecting doesn't leave the page.
 */
class CardOptionSwatches extends HTMLElement {
  /** @type {AbortController | undefined} */
  #abortController;

  /** @type {string | null} Media ID of the clicked swatch, or null while showing the card's original image */
  #selectedMediaId = null;

  /** @type {string | null} Media ID of the image the card first showed */
  #initialMediaId = null;

  connectedCallback() {
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    const firstSlide = this.slideshow?.querySelector('slideshow-slide[slide-id]:not([hidden])');
    this.#initialMediaId = firstSlide?.getAttribute('slide-id') ?? null;

    // Start loading the frame images once someone shows interest in the card, so switching is instant
    for (const type of ['pointerenter', 'focusin', 'touchstart']) {
      this.card?.addEventListener(type, this.#preload, { signal, once: true, passive: true });
    }

    this.addEventListener('click', this.#handleClick, { signal });
    this.addEventListener('pointerover', this.#handlePointerOver, { signal });
    this.addEventListener('pointerleave', this.#restore, { signal });

    // The card's own hover handlers step through slides on pointerleave; put the chosen frame back afterwards
    this.card?.addEventListener(
      'pointerleave',
      () => {
        if (this.#selectedMediaId) requestAnimationFrame(this.#restore);
      },
      { signal }
    );
  }

  disconnectedCallback() {
    this.#abortController?.abort();
  }

  get card() {
    return this.closest('product-card');
  }

  get slideshow() {
    return /** @type {any} */ (this.card?.querySelector('slideshow-component'));
  }

  /** @param {Event} event */
  #handleClick = (event) => {
    const swatch = event.target instanceof Element ? event.target.closest('.card-option-swatch') : null;
    if (!(swatch instanceof HTMLButtonElement)) return;

    event.preventDefault();
    this.#selectedMediaId = swatch.dataset.mediaId ?? null;
    this.#showMedia(this.#selectedMediaId);

    for (const button of this.querySelectorAll('.card-option-swatch')) {
      button.setAttribute('aria-pressed', String(button === swatch));
    }

    const url = swatch.dataset.url;
    if (!url || !this.card) return;
    for (const link of this.card.querySelectorAll('a.product-card__link, [ref="cardGalleryLink"], [ref="productTitleLink"]')) {
      if (link instanceof HTMLAnchorElement) link.href = url;
    }
  };

  /** @param {PointerEvent} event */
  #handlePointerOver = (event) => {
    if (event.pointerType !== 'mouse') return;
    const swatch = event.target instanceof Element ? event.target.closest('.card-option-swatch') : null;
    if (swatch instanceof HTMLElement) this.#showMedia(swatch.dataset.mediaId ?? null);
  };

  /** Back to the clicked swatch's image, or the card's original image if none was clicked */
  #restore = () => {
    this.#showMedia(this.#selectedMediaId ?? this.#initialMediaId);
  };

  #preload = () => {
    for (const swatch of this.querySelectorAll('.card-option-swatch')) {
      const mediaId = swatch instanceof HTMLElement ? swatch.dataset.mediaId : null;
      const image = this.slideshow?.querySelector(`slideshow-slide[slide-id="${mediaId}"] img`);
      if (image instanceof HTMLImageElement) image.loading = 'eager';
    }
  };

  /** @param {string | null} mediaId */
  #showMedia(mediaId) {
    const slideshow = this.slideshow;
    if (!slideshow || !mediaId) return;

    const slides = slideshow.querySelectorAll('slideshow-slide[slide-id]');
    let target = null;
    for (const slide of slides) {
      if (slide.getAttribute('slide-id') === mediaId) target = slide;
    }
    if (!target) return;

    // Only one variant image is visible at a time, like the theme's own swatches
    for (const slide of slides) {
      if (slide.hasAttribute('variant-image')) slide.hidden = slide !== target;
    }
    target.hidden = false;

    slideshow.select?.({ id: mediaId }, undefined, { animate: false });
  }
}

if (!customElements.get('card-option-swatches')) {
  customElements.define('card-option-swatches', CardOptionSwatches);
}
